import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Search, CheckCircle2, ArrowRight, BarChart3, Scale, Database,
  Compass, Target, Briefcase, TrendingUp, ShieldCheck, LineChart, Clock, Award,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PILLARS = [
  {
    icon: Compass,
    title: 'Analisi Operativa Realistica',
    desc: 'I piani considerano missioni, frequenze di volo e condizioni operative reali, offrendo una visione concreta di come ogni velivolo si comporterà nel contesto specifico del cliente.',
  },
  {
    icon: BarChart3,
    title: 'Valutazione Economica Completa',
    desc: "Ogni piano include l'analisi dei costi diretti e indiretti, con proiezioni sul valore residuo, trasformando dati complessi in informazioni chiare e operative.",
  },
  {
    icon: Scale,
    title: 'Confronto Neutro tra Modelli',
    desc: 'Offriamo valutazioni indipendenti dei velivoli, evidenziando vantaggi e criticità di ciascun modello. I nostri clienti ottengono informazioni imparziali basate su numeri reali.',
  },
  {
    icon: Database,
    title: 'Dati di Mercato Aggiornati',
    desc: "Le nostre analisi si basano su informazioni verificate e aggiornate del mercato aeronautico, eliminando stime ottimistiche e riducendo i rischi connessi all'investimento.",
  },
  {
    icon: Target,
    title: 'Consulenza Strategica Personalizzata',
    desc: "Il nostro valore non è solo il documento: accompagniamo il cliente nell'interpretazione dei dati e nella scelta del velivolo più adatto, garantendo decisioni consapevoli e sicure.",
  },
  {
    icon: Briefcase,
    title: 'Scenari Alternativi',
    desc: 'Confronto tra proprietà diretta, leasing operativo e proprietà frazionata per individuare la formula più conveniente per il tuo profilo operativo e finanziario.',
  },
];

const STEPS = [
  { n: '01', title: 'Briefing', desc: 'Definiamo insieme missioni tipiche, budget e obiettivi strategici.' },
  { n: '02', title: 'Analisi di Mercato', desc: 'Selezioniamo i modelli candidati con dati reali di mercato e performance.' },
  { n: '03', title: 'Modello Economico', desc: "Costruiamo il TCO completo: acquisto, manutenzione, carburante, assicurazione, valore residuo." },
  { n: '04', title: 'Piano & Raccomandazione', desc: 'Ricevi un report chiaro con raccomandazione e supporto fino alla decisione finale.' },
];

const INCLUDED = [
  'Analisi delle missioni operative',
  'Shortlist di modelli candidati',
  'Total Cost of Ownership (TCO) a 5 e 10 anni',
  'Proiezione del valore residuo',
  'Confronto proprietà vs leasing vs frazionata',
  'Report finale e call di debriefing',
];

export default function PianoAcquisizione() {
  const [form, setForm] = useState({
    nome: '',
    cognome: '',
    email: '',
    oggetto: 'Piano di Acquisizione',
    messaggio: '',
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    document.title = 'Piani di Acquisizione Velivolo & Consulenza Strategica | AeroDeals';
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'description');
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', 'Piani di Acquisizione Velivolo AeroDeals: analisi strategica e personalizzata per scegliere il velivolo giusto con dati reali di mercato, TCO completo e confronto neutro tra modelli.');
  }, []);

  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));
  const scrollToForm = () =>
    document.getElementById('form-piano')?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nome || !form.cognome || !form.email) {
      toast.error('Compila tutti i campi obbligatori.');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/consulenze/request`, form);
      setSent(true);
      toast.success('Richiesta inviata. Ti contatteremo a breve.');
      setForm({ nome: '', cognome: '', email: '', oggetto: 'Piano di Acquisizione', messaggio: '' });
    } catch (err) {
      console.error(err);
      toast.error("Errore durante l'invio. Riprova.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090B] pt-20" data-testid="piano-acquisizione-page">
      {/* ─── Hero ─── */}
      <section className="relative py-24 md:py-32 border-b border-zinc-800/50 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#C5A059]/[0.08] via-transparent to-transparent pointer-events-none" />
        <div className="absolute left-[-10%] bottom-[-10%] w-[600px] h-[600px] rounded-full bg-[#C5A059]/[0.04] blur-3xl pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-[1.2fr,1fr] gap-12 items-center">
            <div>
              <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#C5A059] mb-4">Servizi · Piani di Acquisizione</p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-medium text-white mb-6 leading-[1.05]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                Piani di Acquisizione<br /><span className="text-[#C5A059]">Velivolo</span>
              </h1>
              <p className="text-base md:text-lg text-zinc-300 leading-relaxed mb-8 max-w-xl">
                Analisi completa e personalizzata per acquistare il velivolo giusto e ridurre al minimo i rischi
                dell&apos;investimento.
              </p>
              <div className="flex flex-wrap gap-3 mb-10">
                <button
                  onClick={scrollToForm}
                  className="inline-flex items-center gap-2 bg-[#C5A059] text-black px-6 py-3 font-medium text-sm hover:bg-[#D4B271] transition-colors"
                  data-testid="cta-hero-piano"
                >
                  Richiedi il tuo Piano <ArrowRight size={14} />
                </button>
                <a
                  href="#metodo"
                  className="inline-flex items-center gap-2 border border-zinc-700 text-zinc-300 px-6 py-3 font-medium text-sm hover:border-[#C5A059] hover:text-white transition-colors"
                >
                  Come funziona
                </a>
              </div>
              <div className="grid grid-cols-3 gap-4 max-w-lg border-t border-zinc-800 pt-6">
                <div className="flex items-start gap-2.5">
                  <LineChart size={16} className="text-[#C5A059] mt-0.5 shrink-0" />
                  <div>
                    <div className="text-white text-sm font-medium">TCO 5-10y</div>
                    <div className="text-[11px] text-zinc-500 leading-tight">Proiezione completa</div>
                  </div>
                </div>
                <div className="flex items-start gap-2.5">
                  <ShieldCheck size={16} className="text-[#C5A059] mt-0.5 shrink-0" />
                  <div>
                    <div className="text-white text-sm font-medium">Indipendente</div>
                    <div className="text-[11px] text-zinc-500 leading-tight">Zero conflitti</div>
                  </div>
                </div>
                <div className="flex items-start gap-2.5">
                  <Clock size={16} className="text-[#C5A059] mt-0.5 shrink-0" />
                  <div>
                    <div className="text-white text-sm font-medium">&lt; 24h</div>
                    <div className="text-[11px] text-zinc-500 leading-tight">Prima risposta</div>
                  </div>
                </div>
              </div>
            </div>
            {/* Hero visual card */}
            <div className="hidden lg:block">
              <div className="relative border border-zinc-800 bg-gradient-to-br from-zinc-900 to-[#0E0E10] p-8">
                <div className="absolute -top-px left-8 right-8 h-px bg-gradient-to-r from-transparent via-[#C5A059]/40 to-transparent" />
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#C5A059]/15">
                    <Search size={18} className="text-[#C5A059]" />
                  </div>
                  <div>
                    <div className="text-xs font-mono uppercase tracking-widest text-[#C5A059]">Cosa include il piano</div>
                    <div className="text-sm text-zinc-400">Report personalizzato</div>
                  </div>
                </div>
                <ul className="space-y-3.5">
                  {INCLUDED.map((t) => (
                    <li key={t} className="flex items-start gap-3 text-sm text-zinc-300">
                      <CheckCircle2 size={16} className="text-[#C5A059] mt-0.5 shrink-0" />
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-6 pt-6 border-t border-zinc-800 text-xs text-zinc-500">
                  Pensato per imprenditori, aziende e operatori dell&apos;aviazione generale.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Intro narrativa ─── */}
      <section className="py-20 md:py-24 border-b border-zinc-800/50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-[0.8fr,1.2fr] gap-10 lg:gap-16">
            <div>
              <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#C5A059] mb-3">Il Servizio</p>
              <h2 className="text-2xl md:text-3xl tracking-tighter font-medium text-white leading-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                Analisi Strategica e Sicura per i Tuoi Investimenti
              </h2>
            </div>
            <div className="space-y-5 text-zinc-300 leading-relaxed text-[15px]">
              <p>
                Acquistare un aereo è una decisione complessa, che richiede esperienza, dati concreti e una valutazione
                completa dei costi e delle performance. I <strong className="text-white">Piani di Acquisizione Velivolo di AeroDeals</strong> offrono
                consulenza personalizzata e professionale, pensata per guidare imprenditori, aziende e operatori nella
                scelta del velivolo più adatto alle loro esigenze operative e finanziarie. Ogni piano nasce da analisi
                dettagliate basate su scenari reali, dati aggiornati di mercato e comparazioni operative.
              </p>
              <p>
                Ogni cliente riceve un piano sviluppato sulle proprie missioni operative e sulle proprie esigenze,
                trasformando dati complessi in informazioni chiare e immediatamente utilizzabili. La nostra consulenza
                non si limita a indicare numeri: accompagna il cliente nella comprensione delle implicazioni economiche,
                tecniche e logistiche dell&apos;investimento, riducendo il rischio di sorprese e decisioni errate.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Case study / How we transform ─── */}
      <section className="py-20 md:py-24 border-b border-zinc-800/50 bg-zinc-950/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#C5A059] mb-3">Metodo</p>
          <h2 className="text-3xl md:text-4xl tracking-tighter font-medium text-white mb-10 max-w-3xl" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Come AeroDeals trasforma la scelta in sicurezza
          </h2>

          <div className="grid lg:grid-cols-[1.3fr,1fr] gap-8">
            <div className="space-y-5 text-zinc-300 leading-relaxed text-[15px]">
              <p>
                I nostri piani permettono di valutare con precisione i costi di gestione reali, le differenze operative
                tra modelli e le proiezioni finanziarie a medio-lungo termine.
              </p>
              <p>
                Il nostro approccio integra ogni variabile rilevante: costi di manutenzione, assicurazione, carburante e
                hangar, oltre a confronto tra modelli e strategie alternative come leasing operativo o proprietà
                frazionata. Il risultato è un piano completo, scalabile, ma rigorosamente personalizzato.
              </p>
              <p>
                Affidarsi a AeroDeals significa scegliere un partner con esperienza diretta nel settore dell&apos;aviazione
                generale. La nostra consulenza trasforma una scelta complessa in un investimento sicuro e ottimizzato,
                fornendo trasparenza, dati reali e supporto strategico in ogni fase dell&apos;acquisizione.
              </p>
            </div>

            {/* Case study callout */}
            <aside className="border border-[#C5A059]/25 bg-[#C5A059]/[0.04] p-7">
              <div className="flex items-center gap-2 mb-4">
                <TrendingUp size={16} className="text-[#C5A059]" />
                <span className="text-xs font-mono uppercase tracking-widest text-[#C5A059]">Case Study</span>
              </div>
              <h3 className="text-lg font-medium text-white mb-3 leading-snug" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                PC-12 NGX vs Citation CJ1
              </h3>
              <p className="text-sm text-zinc-300 leading-relaxed mb-5">
                In una recente analisi comparativa, il cliente ha scoperto che il turboprop era più efficiente per le
                sue missioni, consentendogli di risparmiare centinaia di migliaia di euro — decisione resa possibile
                grazie ai nostri dati dettagliati su manutenzione, consumi e valore residuo.
              </p>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="border border-zinc-800 py-3">
                  <div className="text-xl text-[#C5A059] font-medium" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>€100k+</div>
                  <div className="text-[10px] text-zinc-500 uppercase tracking-widest">Risparmiati</div>
                </div>
                <div className="border border-zinc-800 py-3">
                  <div className="text-xl text-[#C5A059] font-medium" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>2</div>
                  <div className="text-[10px] text-zinc-500 uppercase tracking-widest">Modelli</div>
                </div>
                <div className="border border-zinc-800 py-3">
                  <div className="text-xl text-[#C5A059] font-medium" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>10y</div>
                  <div className="text-[10px] text-zinc-500 uppercase tracking-widest">Proiezione</div>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* ─── Pillars ─── */}
      <section id="metodo" className="py-20 md:py-24 border-b border-zinc-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#C5A059] mb-3">Perché Funziona</p>
          <h2 className="text-3xl md:text-4xl tracking-tighter font-medium text-white mb-12 max-w-2xl" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Sei pilastri per una decisione sicura
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {PILLARS.map((p, i) => (
              <div key={p.title} className="bg-zinc-900/40 border border-zinc-800 p-6 hover:border-[#C5A059]/40 transition-colors" data-testid={`pillar-${i}`}>
                <div className="w-11 h-11 flex items-center justify-center bg-[#C5A059]/15 mb-4">
                  <p.icon size={20} className="text-[#C5A059]" />
                </div>
                <h3 className="text-base font-medium text-white mb-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{p.title}</h3>
                <p className="text-sm text-zinc-400 leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Steps ─── */}
      <section className="py-20 md:py-24 border-b border-zinc-800/50 bg-zinc-950/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#C5A059] mb-3">Processo</p>
          <h2 className="text-3xl md:text-4xl tracking-tighter font-medium text-white mb-12 max-w-2xl" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Dal briefing al piano operativo in 4 passaggi
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {STEPS.map((s, i) => (
              <div key={s.n} className="relative bg-zinc-900/40 border border-zinc-800 p-6 hover:border-[#C5A059]/40 transition-colors" data-testid={`piano-step-${s.n}`}>
                <div className="text-5xl font-medium text-[#C5A059]/30 mb-3" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{s.n}</div>
                <h3 className="text-lg font-medium text-white mb-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{s.title}</h3>
                <p className="text-sm text-zinc-400 leading-relaxed">{s.desc}</p>
                {i < STEPS.length - 1 && (
                  <ArrowRight size={14} className="hidden lg:block absolute top-8 -right-3 text-zinc-700" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Form ─── */}
      <section id="form-piano" className="py-20 md:py-24 border-b border-zinc-800/50 bg-gradient-to-b from-transparent to-[#C5A059]/[0.03]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-[0.9fr,1.3fr] gap-10 lg:gap-14 items-start">
            <div className="lg:sticky lg:top-28">
              <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#C5A059] mb-3">Richiedi il tuo Piano</p>
              <h2 className="text-3xl md:text-4xl tracking-tighter font-medium text-white mb-5" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                Richiedere un Piano di Acquisizione è semplice
              </h2>
              <p className="text-sm text-zinc-400 leading-relaxed mb-8">
                Richiedi oggi i tuoi Piani di Acquisizione Velivolo personalizzati e prendi una decisione consapevole e
                sicura, supportata da dati concreti, confronto realistico tra modelli e consulenza strategica
                professionale.
              </p>
              <ul className="space-y-3 text-sm text-zinc-300">
                {['Risposta entro 24 ore', 'Analisi indipendente', 'Report scritto consegnato'].map((t) => (
                  <li key={t} className="flex items-center gap-2.5">
                    <CheckCircle2 size={15} className="text-[#C5A059] shrink-0" />
                    {t}
                  </li>
                ))}
              </ul>
            </div>

            <div>
              {sent ? (
                <div className="border border-[#C5A059]/40 bg-[#C5A059]/[0.06] p-10 text-center" data-testid="piano-success">
                  <CheckCircle2 size={42} className="text-[#C5A059] mx-auto mb-4" />
                  <h3 className="text-xl font-medium text-white mb-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                    Richiesta inviata con successo
                  </h3>
                  <p className="text-sm text-zinc-400 mb-6">Grazie! Un nostro consulente ti contatterà al più presto.</p>
                  <button
                    onClick={() => setSent(false)}
                    className="text-xs font-mono uppercase tracking-widest text-[#C5A059] hover:text-[#D4B271] transition-colors"
                  >
                    Invia un&apos;altra richiesta
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} className="bg-zinc-900/60 border border-zinc-800 p-7 md:p-9 space-y-5" data-testid="piano-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-[11px] font-mono uppercase tracking-widest text-zinc-400 mb-2">Nome *</label>
                      <input type="text" value={form.nome} onChange={update('nome')} required className="w-full bg-zinc-950 border border-zinc-800 px-3 py-2.5 text-sm text-white outline-none focus:border-[#C5A059] transition-colors" data-testid="piano-nome" />
                    </div>
                    <div>
                      <label className="block text-[11px] font-mono uppercase tracking-widest text-zinc-400 mb-2">Cognome *</label>
                      <input type="text" value={form.cognome} onChange={update('cognome')} required className="w-full bg-zinc-950 border border-zinc-800 px-3 py-2.5 text-sm text-white outline-none focus:border-[#C5A059] transition-colors" data-testid="piano-cognome" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-[11px] font-mono uppercase tracking-widest text-zinc-400 mb-2">Email *</label>
                    <input type="email" value={form.email} onChange={update('email')} required className="w-full bg-zinc-950 border border-zinc-800 px-3 py-2.5 text-sm text-white outline-none focus:border-[#C5A059] transition-colors" data-testid="piano-email" />
                  </div>
                  <div>
                    <label className="block text-[11px] font-mono uppercase tracking-widest text-zinc-400 mb-2">Oggetto *</label>
                    <input type="text" value={form.oggetto} onChange={update('oggetto')} required className="w-full bg-zinc-950 border border-zinc-800 px-3 py-2.5 text-sm text-white outline-none focus:border-[#C5A059] transition-colors" data-testid="piano-oggetto" />
                  </div>
                  <div>
                    <label className="block text-[11px] font-mono uppercase tracking-widest text-zinc-400 mb-2">Messaggio</label>
                    <textarea rows={5} value={form.messaggio} onChange={update('messaggio')} placeholder="Descrivi missioni tipiche, budget e modelli di interesse (opzionale)" className="w-full bg-zinc-950 border border-zinc-800 px-3 py-2.5 text-sm text-white outline-none focus:border-[#C5A059] transition-colors resize-none" data-testid="piano-messaggio" />
                  </div>
                  <button type="submit" disabled={loading} className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#C5A059] text-black px-8 py-3 font-medium text-sm hover:bg-[#D4B271] transition-colors disabled:opacity-60 disabled:cursor-not-allowed" data-testid="piano-submit">
                    {loading ? 'Invio in corso...' : 'Richiedi Consulenza'}
                    {!loading && <ArrowRight size={14} />}
                  </button>
                  <p className="text-xs text-zinc-500 leading-relaxed">
                    Inviando la richiesta accetti la nostra <a href="/privacy" className="text-[#C5A059] hover:underline">Privacy Policy</a>.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ─── Outro ─── */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <blockquote className="border-l-2 border-[#C5A059] pl-6 text-zinc-300 leading-relaxed">
            <strong className="text-white">Scopri tutti i velivoli attualmente disponibili</strong> visitando la nostra pagina{' '}
            <a href="/listings" className="text-[#C5A059] hover:underline">Annunci</a>. Trova facilmente il velivolo
            più adatto alle tue esigenze, messaggia con il venditore e concludi l&apos;affare con AeroDeals.
          </blockquote>
        </div>
      </section>
    </div>
  );
}
